import { Grid, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Layout } from '@roc/feature-app-core';
import { ActionsColumn, DataGrid, LinkColumn, StatusChip, StatusType, Tabs } from '@roc/ui';
import { Roles, SupportRoles } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useCommunicationRoutesConfig } from '@roc/feature-communication';
import { AddLoanTaskDialog, useGeneralTasksStore, TaskPriority } from '@roc/feature-general-tasks';
import { useRocInternalPortalStore } from 'apps/roc-internal-portal/src/app/hooks';
import {
  responseManagementColumns,
  DEFAULT_SORT_MODEL,
  ResponseManagementTab,
  ResponseManagementCategoryTab,
  RESPONSE_MANAGEMENT_CATEGORY_BY_TAB,
  TABS,
  CATEGORY_TABS,
} from '../utils';
import {
  loansWaitingUnderwriteTaskColumns,
  DEFAULT_SORT_MODEL as LOANS_WAITING_UNDERWRITE_TASK_DEFAULT_SORT_MODEL,
} from '../../loansWaitingUnderwriteTask/utils';
import {
  otherCriticalColumns,
  DEFAULT_SORT_MODEL as OTHER_CRITICAL_DEFAULT_SORT_MODEL,
} from '../../otherCritical/utils';
import {
  tamariskDashboardColumns,
  DEFAULT_SORT_MODEL as TAMARISK_DEFAULT_SORT_MODEL,
} from '../../tamariskDashboard/utils';
import {
  internalValuationCriticalColumns,
  DEFAULT_SORT_MODEL as INTERNAL_VALUATION_CRITICAL_DEFAULT_SORT_MODEL,
} from '../../internalValuationCritical/utils';
import {
  constructionManagementColumns,
  DEFAULT_SORT_MODEL as CONSTRUCTION_MANAGEMENT_DEFAULT_SORT_MODEL,
} from '../../constructionManagement/utils';
import {
  closingLoansDashboardColumns,
  CLOSING_LOANS_SUB_TABS,
  DEFAULT_SORT_MODEL as CLOSING_LOANS_DEFAULT_SORT_MODEL,
} from '../../closingLoansDashboard/utils';
import {
  titleCommunicationColumns,
  DEFAULT_SORT_MODEL as TITLE_COMMUNICATION_DEFAULT_SORT_MODEL,
} from '../../titleCommunication/utils';

const useStyles = makeStyles(() => ({
  grid: {
    height: '70vh',
  },
  categoryTabs: {
    marginBottom: '8px',
  },
}));

export const ResponseManagement = observer(() => {
  const classes = useStyles();
  const { responseManagementStore, loansWaitingUnderwriteTaskStore, otherCriticalStore, tamariskDashboardStore, internalValuationCriticalStore, constructionManagementStore, closingLoansDashboardStore, titleCommunicationStore, userStore } = useRocInternalPortalStore();
  const { gridStore: responseManagementGridStore } = responseManagementStore;
  const { gridStore: loansWaitingUnderwriteTaskGridStore } = loansWaitingUnderwriteTaskStore;
  const { gridStore: otherCriticalGridStore } = otherCriticalStore;
  const { gridStore: tamariskDashboardGridStore } = tamariskDashboardStore;
  const { gridStore: internalValuationCriticalGridStore } = internalValuationCriticalStore;
  const { gridStore: constructionManagementGridStore } = constructionManagementStore;
  const { gridStore: closingLoansGridStore } = closingLoansDashboardStore;
  const { gridStore: titleCommunicationGridStore } = titleCommunicationStore;
  const history = useHistory();
  const { communicationRoutesConfig } = useCommunicationRoutesConfig();
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;

  const [selectedTab, setSelectedTab] = useState(ResponseManagementTab.RESPONSE_MANAGEMENT);
  const [selectedCategoryTab, setSelectedCategoryTab] = useState(ResponseManagementCategoryTab.UNDERWRITING);
  const [selectedClosingSubTab, setSelectedClosingSubTab] = useState(closingLoansDashboardStore.selectedSubTab);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [currentActionsRow, setCurrentActionsRow] = useState(null);
  const [readyToRender, setReadyToRender] = useState(false);
  const [createCriticalTaskDialogOpen, setCreateCriticalTaskDialogOpen] = useState(false);

  const canMarkConversationAsHandled =
    readyToRender &&
    (userStore.userInformation?.roles?.includes(Roles.BACK_OFFICE_PROCESSOR) ||
      userStore.userInformation?.roles?.includes(SupportRoles.TECH) ||
      userStore.userInformation?.roles?.includes(SupportRoles.TECH_ADMIN) ||
      userStore.userInformation?.roles?.includes(Roles.UNDERWRITER_PRODUCT));

  const canMarkConversationAsHandledRef = useRef(canMarkConversationAsHandled);
  canMarkConversationAsHandledRef.current = canMarkConversationAsHandled;

  useEffect(() => {
    console.log('[ResponseManagement] userInformation:', userStore.userInformation, '- calling getUserInfoAndEntitlements()');
    userStore.getUserInfoAndEntitlements(() => {
      console.log('[ResponseManagement] getUserInfoAndEntitlements loaded, roles:', userStore.userInformation?.roles);
      setReadyToRender(true);
    });
  }, []);

  console.log('[ResponseManagement] readyToRender:', readyToRender, 'canMarkConversationAsHandled:', canMarkConversationAsHandled, 'roles:', userStore.userInformation?.roles);

  const openActionsMenu = (e, row) => {
    setCurrentActionsRow(row);
    setActionsAnchorEl(e.currentTarget);
  };
  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  const handleMarkConversationAsHandled = () => {
    closeActionsMenu();
    if (currentActionsRow?.twilConversationSid) {
      responseManagementStore.markConversationAsHandled(currentActionsRow.twilConversationSid);
    }
  };

  const handleRespond = () => {
    closeActionsMenu();
    if (currentActionsRow?.loanId && currentActionsRow?.twilConversationSid) {
      history.push(
        communicationRoutesConfig
          .conversations(`${currentActionsRow.loanId}`)
          .children.conversation(currentActionsRow.twilConversationSid).url
      );
    }
  };

  const handleOpenCreateCriticalTask = () => {
    closeActionsMenu();
    setCreateCriticalTaskDialogOpen(true);
  };

  const handleCloseCreateCriticalTask = () => {
    setCreateCriticalTaskDialogOpen(false);
  };

  useEffect(() => {
    if (selectedTab === ResponseManagementTab.LOANS_WAITING_UNDERWRITE_TASK) {
      loansWaitingUnderwriteTaskGridStore.reset();
      loansWaitingUnderwriteTaskGridStore.setSortModel(LOANS_WAITING_UNDERWRITE_TASK_DEFAULT_SORT_MODEL);
    } else if (selectedTab === ResponseManagementTab.OTHER_CRITICAL) {
      otherCriticalGridStore.reset();
      otherCriticalGridStore.setSortModel(OTHER_CRITICAL_DEFAULT_SORT_MODEL);
    } else if (selectedTab === ResponseManagementTab.TAMARISK) {
      tamariskDashboardGridStore.reset();
      tamariskDashboardGridStore.setSortModel(TAMARISK_DEFAULT_SORT_MODEL);
    } else if (selectedTab === ResponseManagementTab.INTERNAL_VALUATION_CRITICAL) {
      internalValuationCriticalGridStore.reset();
      internalValuationCriticalGridStore.setSortModel(INTERNAL_VALUATION_CRITICAL_DEFAULT_SORT_MODEL);
    } else if (selectedTab === ResponseManagementTab.CONSTRUCTION_MANAGEMENT) {
      constructionManagementGridStore.reset();
      constructionManagementGridStore.setSortModel(CONSTRUCTION_MANAGEMENT_DEFAULT_SORT_MODEL);
    } else if (selectedTab === ResponseManagementTab.CLOSING_LOANS) {
      closingLoansDashboardStore.setSelectedSubTab(selectedClosingSubTab);
      closingLoansGridStore.reset();
      closingLoansGridStore.setSortModel(CLOSING_LOANS_DEFAULT_SORT_MODEL);
    } else if (selectedTab === ResponseManagementTab.TITLE_COMMUNICATION) {
      titleCommunicationGridStore.reset();
      titleCommunicationGridStore.setSortModel(TITLE_COMMUNICATION_DEFAULT_SORT_MODEL);
    } else {
      responseManagementGridStore.reset();
      responseManagementStore.setSelectedCategory(RESPONSE_MANAGEMENT_CATEGORY_BY_TAB[selectedCategoryTab]);
      responseManagementGridStore.setSortModel(DEFAULT_SORT_MODEL);
    }
  }, [selectedTab, selectedCategoryTab, selectedClosingSubTab]);

  const frameworkComponents = {
    loanIdCellRenderer: params => (
      <LinkColumn
        value={params.value}
        url={{ pathname: '/loan_information/' + params.value }}
        onClick={e => {
          open('/nportal/#/loan_information/' + params.value);
          e.preventDefault();
        }}
      />
    ),
    responseManagementActionsCellRenderer: params =>
      canMarkConversationAsHandledRef.current
        ? ActionsColumn({
          actions: [
            {
              icon: <MoreVertIcon color="primary" />,
              onClick: e => openActionsMenu(e, params.node.data),
            },
          ],
        })
        : null,
    titleCommunicationActionsCellRenderer: params =>
      canMarkConversationAsHandledRef.current
        ? ActionsColumn({
          actions: [
            {
              icon: <MoreVertIcon color="primary" />,
              onClick: e => openActionsMenu(e, params.node.data),
            },
          ],
        })
        : null,
    loansWaitingUnderwriteTaskActionsCellRenderer: params =>
      canMarkConversationAsHandledRef.current
        ? ActionsColumn({
          actions: [
            {
              icon: <MoreVertIcon color="primary" />,
              onClick: e => openActionsMenu(e, params.node.data),
            },
          ],
        })
        : null,
    priorityCellRenderer: params =>
      !!params.value && (
        <StatusChip statusType={StatusType.PRIORITY} label={params.value} size="small" />
      ),
    internalValuationStatusCellRenderer: params =>
      !!params.value && (
        <StatusChip label={params.value} size="small" statusType={StatusType.LOAN} />
      ),
    internalValuationSubStatusCellRenderer: params =>
      !!params.value && (
        <StatusChip label={params.value} size="small" statusType={StatusType.SUB_STATUS} />
      ),
  };

  const onResponseManagementSortChanged = params => {
    responseManagementGridStore.setSortModel(params.api.getSortModel());
  };

  const onResponseManagementFilterChanged = params => {
    responseManagementGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onLoansWaitingUnderwriteTaskSortChanged = params => {
    loansWaitingUnderwriteTaskGridStore.setSortModel(params.api.getSortModel());
  };

  const onLoansWaitingUnderwriteTaskFilterChanged = params => {
    loansWaitingUnderwriteTaskGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onOtherCriticalSortChanged = params => {
    otherCriticalGridStore.setSortModel(params.api.getSortModel());
  };

  const onOtherCriticalFilterChanged = params => {
    otherCriticalGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onTamariskDashboardSortChanged = params => {
    tamariskDashboardGridStore.setSortModel(params.api.getSortModel());
  };

  const onTamariskDashboardFilterChanged = params => {
    tamariskDashboardGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onInternalValuationCriticalSortChanged = params => {
    internalValuationCriticalGridStore.setSortModel(params.api.getSortModel());
  };

  const onInternalValuationCriticalFilterChanged = params => {
    internalValuationCriticalGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onConstructionManagementSortChanged = params => {
    constructionManagementGridStore.setSortModel(params.api.getSortModel());
  };

  const onConstructionManagementFilterChanged = params => {
    constructionManagementGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onTitleCommunicationSortChanged = params => {
    titleCommunicationGridStore.setSortModel(params.api.getSortModel());
  };

  const onTitleCommunicationFilterChanged = params => {
    titleCommunicationGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onClosingLoansSortChanged = params => {
    closingLoansGridStore.setSortModel(params.api.getSortModel());
  };

  const onClosingLoansFilterChanged = params => {
    closingLoansGridStore.setFilterModel(params.api.getFilterModel());
  };

  if (!readyToRender) {
    return <Layout title="Response Management">{null}</Layout>;
  }

  return (
    <Layout title="Response Management">
      <Grid container>
        <Grid item xs={12}>
          <Tabs
            selectedTab={selectedTab}
            tabs={TABS}
            onTabChange={(e, i) => setSelectedTab(i)}
          />
        </Grid>
        {selectedTab === ResponseManagementTab.RESPONSE_MANAGEMENT && (
          <>
            <Grid item xs={12} className={classes.categoryTabs}>
              <Tabs
                selectedTab={selectedCategoryTab}
                tabs={CATEGORY_TABS}
                onTabChange={(e, i) => setSelectedCategoryTab(i)}
              />
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                className={classes.grid}
                columns={responseManagementColumns}
                rows={responseManagementGridStore.gridData.data.rows}
                frameworkComponents={frameworkComponents}
                onSortChanged={onResponseManagementSortChanged}
                onFilterChanged={onResponseManagementFilterChanged}
                sortModel={responseManagementGridStore.sortModel}
                isLoading={responseManagementGridStore.isLoading}
                showFilters
                showPagination
                paginationData={responseManagementGridStore.gridData.meta}
                setPageNumber={responseManagementGridStore.setPageNumber}
              />
            </Grid>
          </>
        )}
        {selectedTab === ResponseManagementTab.LOANS_WAITING_UNDERWRITE_TASK && (
          <Grid item xs={12}>
            <DataGrid
              className={classes.grid}
              columns={loansWaitingUnderwriteTaskColumns}
              rows={loansWaitingUnderwriteTaskGridStore.gridData.data.rows}
              frameworkComponents={frameworkComponents}
              onSortChanged={onLoansWaitingUnderwriteTaskSortChanged}
              onFilterChanged={onLoansWaitingUnderwriteTaskFilterChanged}
              sortModel={loansWaitingUnderwriteTaskGridStore.sortModel}
              isLoading={loansWaitingUnderwriteTaskGridStore.isLoading}
              showFilters
              showPagination
              paginationData={loansWaitingUnderwriteTaskGridStore.gridData.meta}
              setPageNumber={loansWaitingUnderwriteTaskGridStore.setPageNumber}
            />
          </Grid>
        )}
        {selectedTab === ResponseManagementTab.OTHER_CRITICAL && (
          <Grid item xs={12}>
            <DataGrid
              className={classes.grid}
              columns={otherCriticalColumns}
              rows={otherCriticalGridStore.gridData.data.rows}
              frameworkComponents={frameworkComponents}
              onSortChanged={onOtherCriticalSortChanged}
              onFilterChanged={onOtherCriticalFilterChanged}
              sortModel={otherCriticalGridStore.sortModel}
              isLoading={otherCriticalGridStore.isLoading}
              showFilters
              showPagination
              paginationData={otherCriticalGridStore.gridData.meta}
              setPageNumber={otherCriticalGridStore.setPageNumber}
            />
          </Grid>
        )}
        {selectedTab === ResponseManagementTab.TAMARISK && (
          <Grid item xs={12}>
            <DataGrid
              className={classes.grid}
              columns={tamariskDashboardColumns}
              rows={tamariskDashboardGridStore.gridData.data.rows}
              frameworkComponents={frameworkComponents}
              onSortChanged={onTamariskDashboardSortChanged}
              onFilterChanged={onTamariskDashboardFilterChanged}
              sortModel={tamariskDashboardGridStore.sortModel}
              isLoading={tamariskDashboardGridStore.isLoading}
              showFilters
              showPagination
              paginationData={tamariskDashboardGridStore.gridData.meta}
              setPageNumber={tamariskDashboardGridStore.setPageNumber}
            />
          </Grid>
        )}
        {selectedTab === ResponseManagementTab.INTERNAL_VALUATION_CRITICAL && (
          <Grid item xs={12}>
            <DataGrid
              className={classes.grid}
              columns={internalValuationCriticalColumns}
              rows={internalValuationCriticalGridStore.gridData.data.rows}
              frameworkComponents={frameworkComponents}
              onSortChanged={onInternalValuationCriticalSortChanged}
              onFilterChanged={onInternalValuationCriticalFilterChanged}
              sortModel={internalValuationCriticalGridStore.sortModel}
              isLoading={internalValuationCriticalGridStore.isLoading}
              showFilters
              showPagination
              paginationData={internalValuationCriticalGridStore.gridData.meta}
              setPageNumber={internalValuationCriticalGridStore.setPageNumber}
            />
          </Grid>
        )}
        {selectedTab === ResponseManagementTab.CONSTRUCTION_MANAGEMENT && (
          <Grid item xs={12}>
            <DataGrid
              className={classes.grid}
              columns={constructionManagementColumns}
              rows={constructionManagementGridStore.gridData.data.rows}
              frameworkComponents={frameworkComponents}
              onSortChanged={onConstructionManagementSortChanged}
              onFilterChanged={onConstructionManagementFilterChanged}
              sortModel={constructionManagementGridStore.sortModel}
              isLoading={constructionManagementGridStore.isLoading}
              showFilters
              showPagination
              paginationData={constructionManagementGridStore.gridData.meta}
              setPageNumber={constructionManagementGridStore.setPageNumber}
            />
          </Grid>
        )}
        {selectedTab === ResponseManagementTab.CLOSING_LOANS && (
          <>
            <Grid item xs={12} className={classes.categoryTabs}>
              <Tabs
                selectedTab={selectedClosingSubTab}
                tabs={CLOSING_LOANS_SUB_TABS}
                onTabChange={(e, i) => setSelectedClosingSubTab(i)}
              />
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                className={classes.grid}
                columns={closingLoansDashboardColumns}
                rows={closingLoansGridStore.gridData.data.rows}
                frameworkComponents={frameworkComponents}
                onSortChanged={onClosingLoansSortChanged}
                onFilterChanged={onClosingLoansFilterChanged}
                sortModel={closingLoansGridStore.sortModel}
                isLoading={closingLoansGridStore.isLoading}
                showFilters
                showPagination
                paginationData={closingLoansGridStore.gridData.meta}
                setPageNumber={closingLoansGridStore.setPageNumber}
              />
            </Grid>
          </>
        )}
        {selectedTab === ResponseManagementTab.TITLE_COMMUNICATION && (
          <Grid item xs={12}>
            <DataGrid
              className={classes.grid}
              columns={titleCommunicationColumns}
              rows={titleCommunicationGridStore.gridData.data.rows}
              frameworkComponents={frameworkComponents}
              onSortChanged={onTitleCommunicationSortChanged}
              onFilterChanged={onTitleCommunicationFilterChanged}
              sortModel={titleCommunicationGridStore.sortModel}
              isLoading={titleCommunicationGridStore.isLoading}
              showFilters
              showPagination
              paginationData={titleCommunicationGridStore.gridData.meta}
              setPageNumber={titleCommunicationGridStore.setPageNumber}
            />
          </Grid>
        )}
      </Grid>
      <Menu
        id="response-management-actions-menu"
        anchorEl={actionsAnchorEl}
        keepMounted
        open={Boolean(actionsAnchorEl)}
        onClose={closeActionsMenu}
      >
        {selectedTab === ResponseManagementTab.RESPONSE_MANAGEMENT && [
          <MenuItem key="open-conversation" onClick={handleRespond}>Open Conversation</MenuItem>,
          <MenuItem key="mark-as-handled" onClick={handleMarkConversationAsHandled}>Mark as Handled</MenuItem>,
        ]}
        {selectedTab === ResponseManagementTab.TITLE_COMMUNICATION && (
          <MenuItem onClick={handleRespond}>Open Conversation</MenuItem>
        )}
        {selectedTab === ResponseManagementTab.LOANS_WAITING_UNDERWRITE_TASK && (
          <MenuItem onClick={handleOpenCreateCriticalTask}>Create Critical Task</MenuItem>
        )}
      </Menu>
      <AddLoanTaskDialog
        open={createCriticalTaskDialogOpen}
        loanId={currentActionsRow?.loanId}
        handleClose={handleCloseCreateCriticalTask}
        options={editTaskStore.loanTaskSectionNames.map(category => ({
          value: category.taskCategoryId,
          label: category.displayName,
        }))}
        defaultPriority={TaskPriority.CRITICAL}
      />
    </Layout>
  );
});
